// src/components/FormularioCancion.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  Button,
  Progress,
  message,
  Spin,
} from 'antd';
import {
  getFirestore,
  collection,
  addDoc,
  setDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import {
  parsePhoneNumberFromString,
  getCountries,
  getCountryCallingCode,
} from 'libphonenumber-js';

import 'antd/dist/reset.css';
import './FormularioCancion.css';
import logo from '../assets/logo.png';

const { Option } = Select;
const { useWatch } = Form;

// API base
const API_BASE =
  (import.meta?.env?.VITE_API_BASE) ||
  (process.env.REACT_APP_API_BASE) ||
  'https://crm-usa-cantalab.onrender.com';

// Países
const ALL_COUNTRIES = getCountries().map((iso) => ({
  iso,
  code: getCountryCallingCode(iso),
  flag: iso
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt())),
}));

const pasos = [
  { key: 'purpose',       label: 'Selecciona el propósito de la canción' },
  { key: 'genre',         label: '¿Qué género deseas?' },
  { key: 'artist',        label: '¿Qué artista te gusta de este género?' },
  { key: 'voiceType',     label: '¿Qué tipo de voz quieres en tu canción?' },
  { key: 'includeName',   label: 'Si quieres que se incluya el nombre o apodo, escríbelo aquí.' },
  { key: 'anecdotes',     label: 'Cuéntanos alguna anécdota o frase que te gustaría incluir.' },
  { key: 'requesterName', label: '¿Cuál es tu nombre?' },
  { key: 'phone',         label: '¿Cuál es tu número de WhatsApp?' },
];

export default function FormularioCancion() {
  const [current, setCurrent] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);
  const [form] = Form.useForm();
  const { search } = useLocation();
  const { phone: phoneParam } = useParams();

  const db = getFirestore();
  const containerRef = useRef(null);
  const [selectedCountry, setSelectedCountry] = useState(
    ALL_COUNTRIES.find((c) => c.iso === 'MX') || ALL_COUNTRIES[0]
  );

  // Prefill
  useEffect(() => {
    const params = new URLSearchParams(search);
    const tel = params.get('telefono');
    const nombre = params.get('nombre');
    if (tel) {
      form.setFieldsValue({ phone: tel.replace(/\D/g, '') });
    }
    if (nombre) {
      form.setFieldsValue({ requesterName: nombre });
    }
    if (phoneParam && /^\d+$/.test(phoneParam)) {
      form.setFieldsValue({ phone: phoneParam });
    }
  }, [search, phoneParam, form]);

  // Auto height
  const sendHeight = () => {
    if (containerRef.current) {
      const height = containerRef.current.scrollHeight + 20;
      window.parent.postMessage({ type: 'AUTO_HEIGHT', height }, '*');
    }
  };
  useEffect(() => {
    sendHeight();
    window.addEventListener('resize', sendHeight);
    return () => window.removeEventListener('resize', sendHeight);
  }, []);
  useEffect(() => {
    const t = setTimeout(sendHeight, 50);
    return () => clearTimeout(t);
  }, [current, showCustom, submitting]);

  const handleSelectChange = (val) => {
    setShowCustom(val === 'Otro');
    if (val !== 'Otro') form.setFieldsValue({ purposeCustom: undefined });
  };

  const handleCountryChange = (countryCode) => {
    const match = ALL_COUNTRIES.find((c) => c.code === countryCode);
    if (match) setSelectedCountry(match);
  };

  const next = () => {
    const field =
      current === 0
        ? showCustom ? 'purposeCustom' : 'purpose'
        : pasos[current].key;
    form.validateFields([field])
      .then(() => setCurrent(current + 1))
      .catch(() => {});
  };
  const prev = () => setCurrent(current - 1);

  const watchedPhone = useWatch('phone', form);
  const watchedCC = useWatch('countryCode', form);
  const displayPhone = phoneParam || watchedPhone || '';

  // Detectar país
  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then((res) => res.json())
      .then((data) => {
        const match = ALL_COUNTRIES.find((c) => c.iso === data.country_code);
        if (match) {
          setSelectedCountry(match);
          form.setFieldsValue({ countryCode: match.code });
        }
      })
      .catch(() => {
        message.warning('No se pudo detectar tu país automáticamente.');
      });
  }, [form]);

  // Validar si ya existe el teléfono
  useEffect(() => {
    async function checkPhone() {
      const full = phoneParam
        ? phoneParam
        : (() => {
            if (!watchedPhone) return '';
            const raw = watchedPhone.replace(/\D/g, '');
            return watchedCC === '52' ? `521${raw}` : `${watchedCC}${raw}`;
          })();
      if (!full) {
        setPhoneExists(false);
        return;
      }
      const letrasQ = query(
        collection(db, 'musica'),
        where('leadPhone', '==', full)
      );
      const snapL = await getDocs(letrasQ);
      setPhoneExists(!snapL.empty);
    }
    checkPhone();
  }, [watchedPhone, watchedCC, phoneParam, db]);

  // onFinish
  const onFinish = async () => {
    setSubmitting(true);
    try {
      const fv = form.getFieldsValue(true);

      // Validar pasos
      for (let i = 0; i < pasos.length; i++) {
        let key = pasos[i].key;
        if (key === 'purpose' && fv.purpose === 'Otro') key = 'purposeCustom';
        if (!fv[key]) {
          message.error('Por favor completa todos los pasos antes de enviar.');
          setSubmitting(false);
          return;
        }
      }

      if (phoneExists) {
        message.error('No puedes enviar: este número ya está registrado.');
        setSubmitting(false);
        return;
      }

      // Normalizar teléfono
      let e164;
      if (phoneParam && /^\d+$/.test(phoneParam)) {
        e164 = phoneParam;
      } else {
        const raw = fv.phone.replace(/\D/g, '');
        const prefix = fv.countryCode === '52' ? '521' : fv.countryCode;
        e164 = `${prefix}${raw}`;
      }
      const jid = `${e164}@s.whatsapp.net`;

      // Crear o actualizar lead
      const leadRef = doc(db, 'leads', jid);
      const leadSnap = await getDoc(leadRef);
      const trigger = 'MusicaLead';

      if (!leadSnap.exists()) {
        await setDoc(leadRef, {
          telefono: e164,
          nombre: fv.includeName || '',
          requesterName: fv.requesterName || '',
          source: 'Formulario',
          fecha_creacion: new Date(),
          estado: 'nuevo',
          etiquetas: [trigger],
          unreadCount: 0,
          lastMessageAt: new Date(),
        });
      } else {
        await updateDoc(leadRef, {
          etiquetas: arrayUnion(trigger),
          lastMessageAt: new Date(),
        });
      }

      // Guardar petición en /musica
      const letraRef = await addDoc(collection(db, 'musica'), {
        leadPhone: e164,
        leadId: jid,
        purpose: fv.purpose === 'Otro' ? fv.purposeCustom : fv.purpose,
        genre: fv.genre,
        artist: fv.artist,
        voiceType: fv.voiceType,
        includeName: fv.includeName,
        anecdotes: fv.anecdotes,
        requesterName: fv.requesterName,
        status: 'Sin letra',
        createdAt: serverTimestamp(),
      });

      await updateDoc(leadRef, { letraIds: arrayUnion(letraRef.id) });

      // Disparar after-form → empatía + secuencia MusicaLead
      try {
        const summary = {
          nombre: fv.includeName || '',
          requesterName: fv.requesterName || '',
          proposito: fv.purpose === 'Otro' ? fv.purposeCustom : fv.purpose,
          genero: fv.genre || '',
          artista: fv.artist || '',
          anecdotas: fv.anecdotes || '',
        };

        const r = await fetch(`${API_BASE}/api/lead/after-form`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ leadId: jid, summary }),
        });

        if (!r.ok) {
          const txt = await r.text().catch(() => '');
          console.warn('after-form no OK:', r.status, txt);
        }
      } catch (e) {
        console.warn('after-form falló:', e);
      }

      // Redirigir
      const redirectUrl = 'https://cantalab.com/unete-a-la-comunidad/';
      if (window.self !== window.top) {
        window.parent.location.href = redirectUrl;
      } else {
        window.location.href = redirectUrl;
      }
    } catch (err) {
      console.error(err);
      message.error('Error al procesar tu solicitud.');
      setSubmitting(false);
    }
  };

  const percent = Math.round(((current + 1) / pasos.length) * 100);

  return (
    <div
      className="formulario-cancion"
      ref={containerRef}
      style={{ maxWidth: 600, margin: '40px auto', padding: '0 20px 20px' }}
    >
      <div style={{ textAlign: 'center', marginBottom: 24, paddingTop: 20 }}>
        <img src={logo} alt="Cantalab Logo" style={{ width: 200, marginTop: -50, marginBottom: 16 }} />
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700' }}>
          Haz que tus sentimientos se escuchen
          <span style={{ fontWeight: 800, color: '#5E4CAE' }}> con tu canción personalizada.</span>
        </h1>
        <h2 style={{ margin: '8px 0 0', fontWeight: '700', color: '#ff5300' }}>
          Hacemos GRATIS tu canción. Llena el formulario.👇
        </h2>
      </div>

      <Progress percent={percent} showInfo={false} strokeLinecap="square" style={{ marginBottom: 24 }} />

      <div style={{ textAlign: 'center', marginBottom: 8, color: '#888' }}>
        {current + 1} of {pasos.length}
      </div>
      <h2 style={{ fontSize: '1.75rem', textAlign: 'center', marginBottom: 24 }}>
        {pasos[current].label}
        {pasos[current].key !== 'includeName' && <span style={{ color: 'red' }}> *</span>}
      </h2>

      <Spin spinning={submitting} tip="Enviando">
        <Form form={form} layout="vertical" onFinish={onFinish}>
          {pasos[current].key === 'phone' && (
            <div style={{ margin: '24px 0', textAlign: 'center', fontSize: 16 }}>
              {!phoneExists ? (
                <p>
                  🎉 <strong>Listo!</strong> Da click en enviar y recibirás tu canción en tu WhatsApp <strong>{displayPhone}</strong>.
                </p>
              ) : (
                <p style={{ color: 'red' }}>⚠️ Ese número ya registró una canción.</p>
              )}
            </div>
          )}

          {current === 0 && (
            <>
              <Form.Item
                name="purpose"
                rules={[{ required: true, message: 'Selecciona un propósito' }]}
              >
                <Select placeholder="Seleccione propósito" onChange={handleSelectChange}>
                  {[
                    'Declarar mi amor','Decirle que la/o quiero','Desamor',
                    'Motivación y Superación Personal','Homenajes','Memoriales',
                    'Agradecer su Amistad','Despedida','Un Aniversario','Agradecer',
                    'Reconocimientos y Logros','Felicitación por Cumpleaños',
                    'Propuesta de Matrimonio','Nacimiento de un Bebé',
                    'Día de la Madre o del Padre','Otro'
                  ].map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
                </Select>
              </Form.Item>
              {showCustom && (
                <Form.Item
                  name="purposeCustom"
                  rules={[{ required: true, message: 'Escribe tu propósito' }]}
                >
                  <Input.TextArea rows={3} placeholder="Escribe tu propósito" />
                </Form.Item>
              )}
            </>
          )}

          {current > 0 && pasos[current].key !== 'phone' && (() => {
            const fieldKey = pasos[current].key;
            const rules = fieldKey === 'includeName'
              ? []
              : [{ required: true, message: 'Este campo es requerido' }];
            return (
              <Form.Item name={fieldKey} rules={rules}>
                {fieldKey === 'genre' && (
                  <Select placeholder="Selecciona un género">
                    {['Corrido tumbado','Balada Romántica','Regional Mexicano','Pop',
                      'Corrido Norteño','Rock Pop','Reguetón','Bachata','Rap','Salsa',
                      'Rock','Cumbia','Banda','Que un experto elija por mí','Otro'
                    ].map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
                  </Select>
                )}
                {fieldKey === 'voiceType' && (
                  <Select placeholder="Selecciona tipo de voz">
                    {['Voz Femenina','Voz Masculina','Cualquiera'].map(opt => (
                      <Option key={opt} value={opt}>{opt}</Option>
                    ))}
                  </Select>
                )}
                {['artist','includeName','requesterName'].includes(fieldKey) && (
                  <Input placeholder={pasos[current].label} />
                )}
                {fieldKey === 'anecdotes' && (
                  <Input.TextArea rows={3} placeholder={pasos[current].label} />
                )}
              </Form.Item>
            );
          })()}

          <div style={{ marginTop: 32 }}>
            {current < pasos.length - 1 && (
              <Button type="primary" onClick={next} block size="large">
                Continuar ↵
              </Button>
            )}
            {current === pasos.length - 1 && (
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting || phoneExists}
                block
                size="large"
              >
                Enviar y recibir canción 🎵
              </Button>
            )}
            {current > 0 && (
              <Button onClick={prev} block style={{ marginTop: 10 }}>
                ← Anterior
              </Button>
            )}
          </div>
        </Form>
      </Spin>
    </div>
  );
}
